import { inject, Injectable } from '@angular/core';
import { AutopropBackend } from 'app/shared/backend';
import { LocationToken } from 'app/shared/utils';
import { environment } from 'environments/environment';
import { versions } from 'environments/versions';
import { map, Observable } from 'rxjs';
import { AgreementsResponse, AuthLoginResponse, ForgotPasswordResponse, UserInfoResponse } from './response.model';


@Injectable({
    providedIn: 'root',
})
export class AuthApi {

    private backend = inject(AutopropBackend);

    private location: Location = inject(LocationToken);

    setToken(jwt: string) {
        this.backend.setHeaders({ Authorization: `Bearer ${jwt}` });
    }

    setCustomHeader(name: string, value: string) {
        this.backend.setHeaders({ [name]: value });
    }

    getBrowserInfo() {
        return {
            release: versions.revision,
            UA: navigator.userAgent || 'other',
            lang: navigator.language || 'other',
            screen: {
                screenWidth: screen.width || 0,
                screenHeight: screen.height || 0,
            },
        };
    }

    login(username: string, password: string): Observable<AuthLoginResponse> {
        const body = {
            username,
            password,
            extra: this.getBrowserInfo(),
        };
        return this.backend.post('/api/user/login', body, { bypassErrorHandler: true });
    }

    logout() {
        return this.backend.get('/api/user/logout');
    }

    tokenValidate(token: string) {
        return this.backend.post<UserInfoResponse>('/api/user/token-validate', { token: token }, { bypassErrorHandler: true });
    }

    agree(key: string, version: number, ignore: boolean) {
        return this.backend.post<AgreementsResponse>('/api/user/agree', { key, version, ignore }).pipe(map(response => response.payload.agreements));
    }

    refreshUser() {
        return this.backend.get<UserInfoResponse>('/api/user/refresh', { ignoreLoadingBar: true });
    }

    refreshCredentials() {
        return this.backend.get('/api/user/refresh-credentials', { ignoreLoadingBar: true });
    }

    refreshBranding() {
        return this.backend.get('/api/user/branding', { ignoreLoadingBar: true });
    }

    forgotPassword(email: string) {
        return this.backend.post<ForgotPasswordResponse>('/api/user/forgot-password', { email });
    }

    setPassword(password: string, token: string) {
        return this.backend.post('/api/user/set-password', { password, token });
    }

    getCreaLoginLink() {
        const params = new URLSearchParams({
            response_type: 'code',
            client_id: environment.creaClientId,
            redirect_uri: `${this.location.origin}/crea-bridge`,
            scope: 'openid',
            nonce: `n-${Math.floor(Math.random() * 10000)}`,
        });
        return `https://auth.realtor.ca/authorize?${params}`;
    }

    getCreaLogoutLink() {
        const params = new URLSearchParams({
            client_id: environment.creaClientId,
            returnTo: `${this.location.origin}/login`,
        });
        return `https://auth.realtor.ca/v2/logout?${params}`;
    }

    getRelevateLoginLink() {
        const searchParams = new URLSearchParams({
            response_type: 'id_token',
            client_id: environment.relevateClientId,
            redirect_uri: `${this.location.origin}/relevate-bridge`,
            scope: 'openid',
            nonce: `n-${crypto.randomUUID()}`,
        });
        return `https://relevate-vireb.us.auth0.com/authorize?${searchParams}`;
    }
}
